import {
	DropdownMenuPortal,
	Root as DropdownMenuRoot,
	DropdownMenuTrigger
} from '@radix-ui/react-dropdown-menu'
import { useQuery } from '@tanstack/react-query'
import {
	DropdownContent,
	DropdownItem,
	DropdownSeparator
} from '@/components/ui/dropdown-atoms'
import {
	BunIcon,
	CheckIcon,
	DotsHorizontalIcon,
	EditAirbnbIcon,
	EyeIcon
} from '@/components/ui/icons'
import { operatorQueryOptions } from '@/features/marquee/query-options'
import {
	type LocationRowAction,
	locationRowActions
} from '@/features/marquee/utils'
import { cn } from '@/helpers/utils'
import type { MarqueeLocation } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

/** Label left, icon hard right, as in the Team row menu. */
const ITEM_CLASS = 'flex items-center justify-between gap-4'

interface LocationRowMenuProps {
	location: MarqueeLocation
	onAction: (action: LocationRowAction, location: MarqueeLocation) => void
}

/**
 * The Locations row's kebab ([[locationRowActions]]): edit, view its events,
 * then deactivate for an active location and reactivate for a deactivated one.
 *
 * Every item hands the row back to the page, which owns the dialogs, for the
 * same reason as the Team menu: the menu unmounts on select, and the edit form
 * opens the same deactivate confirmation.
 */
export function LocationRowMenu({ location, onAction }: LocationRowMenuProps) {
	const { t } = useTranslation()
	const { data: operator } = useQuery(operatorQueryOptions())
	const actions = locationRowActions(location, operator)

	if (actions.length === 0) {
		return null
	}

	const showSeparator =
		(actions.includes('edit') || actions.includes('view_events')) &&
		(actions.includes('deactivate') || actions.includes('reactivate'))

	return (
		<DropdownMenuRoot>
			<DropdownMenuTrigger asChild>
				<button
					aria-label={t('marquee:locations_row_menu_label')}
					className='flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-neutral-500 transition hover:bg-grey-100 hover:text-neutral-900 data-[state=open]:bg-grey-100 data-[state=open]:text-neutral-900'
					type='button'
				>
					<DotsHorizontalIcon className='rotate-90' size={20} />
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownContent align='end' sideOffset={4}>
					{actions.includes('edit') && (
						<DropdownItem
							className={ITEM_CLASS}
							onClick={() => onAction('edit', location)}
						>
							{t('marquee:locations_action_edit')}
							<EditAirbnbIcon size={20} />
						</DropdownItem>
					)}
					{actions.includes('view_events') && (
						<DropdownItem
							className={ITEM_CLASS}
							onClick={() => onAction('view_events', location)}
						>
							{t('marquee:locations_action_view_events', {
								count: location.event_count
							})}
							<EyeIcon size={20} />
						</DropdownItem>
					)}
					{showSeparator && <DropdownSeparator />}
					{actions.includes('deactivate') && (
						<DropdownItem
							className={cn(ITEM_CLASS, 'text-red-700')}
							onClick={() => onAction('deactivate', location)}
						>
							{t('marquee:locations_action_deactivate')}
							<BunIcon size={20} />
						</DropdownItem>
					)}
					{actions.includes('reactivate') && (
						<DropdownItem
							className={cn(ITEM_CLASS, 'text-green-700')}
							onClick={() => onAction('reactivate', location)}
						>
							{t('marquee:locations_action_reactivate')}
							<CheckIcon size={20} />
						</DropdownItem>
					)}
				</DropdownContent>
			</DropdownMenuPortal>
		</DropdownMenuRoot>
	)
}
